import combineEvents from '@redux-beacon/combine-events';
import { LOCATION_CHANGE } from 'connected-react-router';
import GoogleTagManager from '@redux-beacon/google-tag-manager';
import { trackPageView } from '@redux-beacon/google-analytics';
import { createMiddleware } from 'redux-beacon';
const productsReducer = (arr, productList) => {
    for (let i = 0; i < productList.length; i++) {
        arr[i].quantity += productList[i].quantity;
    }
    return arr;
};
const productMap = (product) => {
    var _a;
    return ({
        item_name: product.title,
        item_id: product.id,
        price: product.price,
        item_brand: product.vendor,
        item_category: (_a = product.category[0]) === null || _a === void 0 ? void 0 : _a.title,
        quantity: product.quantity ? product.quantity : 1,
    });
};
const clear = () => ({ ecommerce: null });
const orderCompleted = (action) => {
    const transactionProducts = action.payload.data && action.payload.data.order && action.payload.data.order.items
        ? action.payload.data.order.items.map((item) => ({
            sku: item.id,
            name: item.title,
            price: item.price,
            quantity: item.numberOfItems,
        }))
        : [];
    return {
        event: 'orderCompleted',
        transactionId: action.payload.data.order.id,
        transactionTotal: action.payload.data.order.paidAmount,
        transactionCurrency: process.env.TRANSACTIONCURRENCY,
        transactionTax: action.payload.data.order.taxAmount,
        transactionShipping: action.payload.data.order.freight
            ? action.payload.data.order.freight.price
            : 0,
        transactionProducts: transactionProducts,
    };
};
const purchase = (action) => {
    const items = action.payload.data && action.payload.data.order && action.payload.data.order.items
        ? action.payload.data.order.items.map((item) => ({
            item_id: item.id,
            item_name: item.title,
            price: item.price,
            quantity: item.numberOfItems,
        }))
        : [];
    return {
        event: 'purchase',
        ecommerce: {
            transaction_id: action.payload.data.order.id,
            value: action.payload.data.order.paidAmount,
            tax: action.payload.data.order.taxAmount,
            shipping: action.payload.data.order.freight
                ? action.payload.data.order.freight.price
                : 0,
            currency: 'NOK',
            items: items,
        },
    };
};
const TwoPhasePurchase = (action) => {
    const productsWithTotalQuantity = action.payload.deliveries
        .map((delivery) => delivery.products)
        .reduce(productsReducer);
    return {
        event: 'two_phase_purchase',
        ecommerce: {
            transaction_id: action.payload.orderId,
            value: action.payload.totalPrice,
            tax: action.payload.vatAmount,
            currency: process.env.TRANSACTIONCURRENCY,
            products: productsWithTotalQuantity,
        },
    };
};
const dimensionRegion = `dimension${process.env.GA_DIMENSION_INDEX_REGION}`;
const eventsMap = {
    [LOCATION_CHANGE]: trackPageView((action, state) => ({
        hitType: 'pageview',
        page: action.payload.location.pathname,
        fieldsObject: {
            [dimensionRegion]: state.region.slug,
        },
    })),
    PLACE_ORDER_SUCCESS: combineEvents(purchase, orderCompleted),
    ANALYTICS_2_PHASE_COMPLETE: (action) => TwoPhasePurchase(action),
    VALIDATE_JWT_SUCCESS: () => ({ event: 'login' }),
    AUTOCOMPLETE_SEARCH: (action) => ({ event: 'search', search_term: action.payload }),
    ADD_TO_CART: (action) => ({
        event: 'add_to_cart',
        ecommerce: {
            currency: 'NOK',
            value: action.payload.price,
            items: [
                {
                    item_name: action.payload.title,
                    item_id: action.payload.id,
                    price: action.payload.price,
                    item_brand: action.payload.vendor,
                    item_category: action.payload.category[0].title,
                    item_list_name: action.payload.productContext,
                    quantity: 1,
                },
            ],
        },
    }),
    REMOVE_FROM_CART: (action) => ({
        event: 'remove_from_cart',
        ecommerce: {
            currency: 'NOK',
            value: action.payload.price,
            items: [
                {
                    item_name: action.payload.title,
                    item_id: action.payload.id,
                    price: action.payload.price,
                    item_brand: action.payload.vendor,
                    item_category: action.payload.category[0].title,
                    item_list_name: action.payload.productContext,
                    quantity: 1,
                },
            ],
        },
    }),
    CANCEL_ORDER_SUCCESS: () => ({ event: 'orderCancelled' }),
    CAN_DELIVER_TO_NEW_ADDRESS: (action) => {
        return {
            event: action.payload.canDeliver ? 'addressAccepted' : 'addressRejected',
        };
    },
    PLACE_SUBSCRIPTION_ORDER_SUCCESS: () => ({ event: 'subscriptionStarted' }),
    COMMIT_EDITED_SUBSCRIPTION_SUCCESS: () => ({ event: 'subscriptionUpdated' }),
    TOGGLE_SUBSCRIPTION_SUCCESS: (action) => ({
        event: action.payload.activate ? 'subscriptionRestarted' : 'subscriptionStopped',
    }),
    BEGIN_CHECKOUT: (action) => {
        const cartState = action.payload;
        return {
            event: 'begin_checkout',
            ecommerce: {
                currency: 'NOK',
                value: cartState.total,
                items: cartState.products.map(productMap),
            },
        };
    },
    ADD_SHIPPING_INFO: (action) => {
        const cartState = action.payload;
        return {
            event: 'add_shipping_info',
            ecommerce: {
                currency: 'NOK',
                value: cartState.total,
                items: cartState.products.map(productMap),
            },
        };
    },
    ADD_PAYMENT_INFO: (action) => {
        const cartState = action.payload;
        return {
            event: 'add_payment_info',
            ecommerce: {
                currency: 'NOK',
                value: cartState.total,
                items: cartState.products.map(productMap),
            },
        };
    },
    SELECT_ITEM: (action) => {
        var _a;
        const { product, productContext } = action.payload;
        return {
            event: 'select_item',
            ecommerce: {
                currency: 'NOK',
                value: product.price,
                items: [
                    {
                        item_name: product.title,
                        item_id: product.id,
                        price: product.price,
                        item_brand: product.vendor,
                        item_category: (_a = product.category[0]) === null || _a === void 0 ? void 0 : _a.title,
                        item_list_name: productContext,
                        quantity: 1,
                    },
                ],
            },
        };
    },
    SELECT_PROMOTION: (action) => {
        const { campaign } = action.payload;
        return {
            event: 'select_promotion',
            ecommerce: {
                promotion_id: campaign.id,
                promotion_name: campaign.title,
            },
        };
    },
    VIEW_ITEM: (action) => {
        var _a;
        const { product, productContext } = action.payload;
        return {
            event: 'view_item',
            ecommerce: {
                currency: 'NOK',
                value: product.price,
                items: [
                    {
                        item_name: product.title,
                        item_id: product.id,
                        price: product.price,
                        item_brand: product.vendor,
                        item_category: (_a = product.category[0]) === null || _a === void 0 ? void 0 : _a.title,
                        item_list_name: productContext,
                        quantity: 1,
                    },
                ],
            },
        };
    },
    VIEW_ITEM_LIST: (action) => {
        const { products, productContext } = action.payload;
        return {
            event: 'view_item_list',
            ecommerce: {
                item_list_name: productContext,
                items: products.map(productMap),
            },
        };
    },
    VIEW_CART: (action) => {
        const cartState = action.payload;
        return {
            event: 'view_cart',
            ecommerce: {
                currency: 'NOK',
                value: cartState.total,
                items: cartState.products.map(productMap),
            },
        };
    },
    GENERATE_LEAD: (action) => {
        const nokValue = action.payload;
        return {
            event: 'generate_lead',
            ecommerce: {
                currency: 'NOK',
                value: nokValue,
            },
        };
    },
};
const gtmOptions = {
    dataLayerName: 'dataLayer',
};
const eventsMapWithClear = Object.fromEntries(Object.keys(eventsMap).map((x) => [x, combineEvents(clear, eventsMap[x])]));
const gtm = GoogleTagManager(gtmOptions);
export const googleAnalyticsMiddleware = createMiddleware(eventsMapWithClear, gtm);
